import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Loader2, RefreshCw, ShieldCheck, AlertTriangle, Trash2, CalendarClock, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export function HealthTab({ api, onChanged, onOpenContactFixes }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [selected, setSelected] = useState({});   // {sectionKey: Set(giftIds)}
  const [fixDate, setFixDate] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/donor-data-health');
      setData(data);
      setSelected({});
    } catch { toast.error('Failed to run data health scan'); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const toggleSel = (key, id) => setSelected(prev => {
    const set = new Set(prev[key] || []);
    if (set.has(id)) set.delete(id); else set.add(id);
    return { ...prev, [key]: set };
  });
  const selectAll = (key, items) => setSelected(prev => {
    const cur = prev[key] || new Set();
    const all = cur.size === items.length;
    return { ...prev, [key]: all ? new Set() : new Set(items.map(i => i.id)) };
  });

  const bulkFix = async (key, action) => {
    const ids = [...(selected[key] || [])];
    if (!ids.length) { toast.error('Select at least one gift first'); return; }
    if (action === 'set_date' && !fixDate) { toast.error('Pick the correct date first'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/donor-gifts/bulk-fix', { action, gift_ids: ids, date: fixDate || undefined });
      toast.success(action === 'delete'
        ? `Deleted ${data.affected} gift${data.affected === 1 ? '' : 's'}`
        : `Re-dated ${data.affected} gift${data.affected === 1 ? '' : 's'} to ${fixDate}`);
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fix failed'); }
    setBusy(false);
  };

  if (loading || !data) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#6B3FA0]" /></div>;

  const issues = data.sections.filter(s => s.count > 0);
  const clean = data.sections.filter(s => s.count === 0);

  return (
    <div className="space-y-3" data-testid="health-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-gray-600">
          Scanned {data.checked_gifts} gifts · {issues.length === 0
            ? 'Everything looks clean 🎉'
            : `${issues.length} issue type${issues.length === 1 ? '' : 's'} need attention`}
        </p>
        <Button size="sm" variant="outline" onClick={load} data-testid="health-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Re-scan
        </Button>
      </div>

      {issues.map(s => {
        const isOpen = open[s.key] !== false; // open by default
        const sel = selected[s.key] || new Set();
        return (
          <Card key={s.key} className="border-amber-200" data-testid={`health-section-${s.key}`}>
            <CardContent className="p-4">
              <button className="w-full flex items-center gap-2 text-left" onClick={() => setOpen(o => ({ ...o, [s.key]: !isOpen }))}
                      data-testid={`health-toggle-${s.key}`}>
                {isOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <span className="font-semibold flex-1">{s.label}</span>
                <Badge className="bg-amber-100 text-amber-800 border-0">{s.count}</Badge>
              </button>
              {isOpen && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-gray-500">{s.hint}</p>
                  {s.key === 'missing_contact' && onOpenContactFixes && (
                    <Button size="sm" variant="outline" onClick={onOpenContactFixes} data-testid="open-contact-fixes-btn">
                      Fix these inline in the Contact Fixes tab →
                    </Button>
                  )}
                  {s.kind === 'gift' && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => selectAll(s.key, s.items)} data-testid={`health-selectall-${s.key}`}>
                        {sel.size === s.items.length && s.items.length > 0 ? 'Unselect all' : `Select all ${s.items.length}`}
                      </Button>
                      <Button size="sm" variant="destructive" disabled={busy || sel.size === 0}
                              onClick={() => bulkFix(s.key, 'delete')} data-testid={`health-delete-${s.key}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete selected ({sel.size})
                      </Button>
                      <div className="flex items-center gap-1">
                        <Input type="date" value={fixDate} onChange={e => setFixDate(e.target.value)}
                               className="h-8 w-40" data-testid={`health-date-input-${s.key}`} />
                        <Button size="sm" variant="outline" disabled={busy || sel.size === 0}
                                onClick={() => bulkFix(s.key, 'set_date')} data-testid={`health-setdate-${s.key}`}>
                          <CalendarClock className="h-3.5 w-3.5 mr-1" /> Set date on selected
                        </Button>
                      </div>
                    </div>
                  )}
                  <div className="max-h-72 overflow-y-auto border rounded-xl divide-y">
                    {s.items.map(it => s.kind === 'gift' ? (
                      <label key={it.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                        <input type="checkbox" checked={sel.has(it.id)} onChange={() => toggleSel(s.key, it.id)}
                               className="h-4 w-4" data-testid={`health-check-${it.id}`} />
                        <button className="font-medium hover:underline truncate" onClick={(e) => { e.preventDefault(); navigate(`/donors/${it.donor_id}`); }}>
                          {it.donor_name || '(unnamed)'}
                        </button>
                        <span className="tabular-nums">{fmt$(it.amount)}</span>
                        <span className="text-gray-500">dated {it.date || '—'}</span>
                        {it.campaign && <Badge variant="outline" className="text-[10px]">{it.campaign}</Badge>}
                        <span className="ml-auto text-xs text-gray-400">imported {it.created_at}</span>
                      </label>
                    ) : (
                      <button key={it.id} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 text-left"
                              onClick={() => navigate(`/donors/${it.id}`)}>
                        <span className="font-medium truncate">{it.name || '(unnamed)'}</span>
                        {it.missing_fields.length > 0 && <span className="text-xs text-amber-700">missing: {it.missing_fields.join(', ')}</span>}
                        <span className="ml-auto text-xs text-gray-500">{fmt$(it.total_given)} lifetime</span>
                      </button>
                    ))}
                  </div>
                  {s.count > s.items.length && <p className="text-[11px] text-gray-400">Showing first {s.items.length} of {s.count}.</p>}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {clean.length > 0 && (
        <Card><CardContent className="p-4">
          <div className="font-semibold mb-2 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Passing checks</div>
          <div className="flex flex-wrap gap-1.5">
            {clean.map(s => (
              <Badge key={s.key} className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`health-clean-${s.key}`}>
                ✓ {s.label}
              </Badge>
            ))}
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}
